import { create } from 'zustand';
import { api } from '../services/api';

export interface LocationUpdate {
  id: string;
  user_id: string;
  latitude: number;
  longitude: number;
  accuracy_metres: number | null;
  battery_level: number | null;
  address: string | null;
  place_name: string | null;
  created_at: string;
  display_name: string;
  colour: string;
}

export interface LocationSettings {
  user_id: string;
  family_id: string;
  sharing_enabled: boolean;
  share_with_members: string[] | null;
}

export interface SafePlace {
  id: string;
  family_id: string;
  name: string;
  latitude: number;
  longitude: number;
  radius_metres: number;
  notify_on_arrival: boolean;
  notify_on_departure: boolean;
  created_by: string;
  created_by_name?: string;
}

export interface GeofenceRule {
  id: string;
  family_id: string;
  member_id: string;
  member_name?: string;
  place_id: string;
  place_name?: string;
  trigger: 'arrival' | 'departure';
  notify_member_ids: string[];
  enabled: boolean;
  created_by: string;
  created_at: string;
}

interface LocatorState {
  locations: LocationUpdate[];
  settings: LocationSettings | null;
  safePlaces: SafePlace[];
  rules: GeofenceRule[];
  isLoading: boolean;
  error: string | null;

  fetchFamilyLocations: () => Promise<void>;
  updateMyLocation: (data: {
    latitude: number;
    longitude: number;
    accuracy_metres?: number;
    battery_level?: number;
    address?: string;
    place_name?: string;
  }) => Promise<void>;
  fetchSettings: () => Promise<void>;
  updateSettings: (data: { sharing_enabled?: boolean; share_with_members?: string[] | null }) => Promise<void>;
  fetchSafePlaces: () => Promise<void>;
  createSafePlace: (data: Partial<SafePlace>) => Promise<void>;
  deleteSafePlace: (id: string) => Promise<void>;
  fetchRules: () => Promise<void>;
  createRule: (data: { member_id: string; place_id: string; trigger: 'arrival' | 'departure'; notify_member_ids: string[] }) => Promise<GeofenceRule>;
  updateRule: (id: string, data: Partial<GeofenceRule>) => Promise<void>;
  deleteRule: (id: string) => Promise<void>;

  // WebSocket handlers
  handleWsMessage: (message: any) => void;
}

export const useLocatorStore = create<LocatorState>((set, get) => ({
  locations: [],
  settings: null,
  safePlaces: [],
  rules: [],
  isLoading: false,
  error: null,

  fetchRules: async () => {
    try {
      const res = await api.get('/locator/rules');
      set({ rules: res.data.rules || [] });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  createRule: async (data) => {
    const res = await api.post('/locator/rules', data);
    const rule: GeofenceRule = res.data.rule;
    set(state => ({ rules: [rule, ...state.rules] }));
    return rule;
  },

  updateRule: async (id, data) => {
    const res = await api.put(`/locator/rules/${id}`, data);
    const rule: GeofenceRule = res.data.rule;
    set(state => ({ rules: state.rules.map(r => r.id === id ? { ...r, ...rule } : r) }));
  },

  deleteRule: async (id) => {
    await api.delete(`/locator/rules/${id}`);
    set(state => ({ rules: state.rules.filter(r => r.id !== id) }));
  },

  fetchFamilyLocations: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get('/locator/family');
      set({ locations: res.data.locations, isLoading: false });
    } catch (err: any) {
      set({ error: err.response?.data?.error || err.message, isLoading: false });
    }
  },

  updateMyLocation: async (data) => {
    try {
      await api.post('/locator/update', data);
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message;
      set({ error: msg });
    }
  },

  fetchSettings: async () => {
    try {
      const res = await api.get('/locator/settings');
      set({ settings: res.data.settings });
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message;
      set({ error: msg });
    }
  },

  updateSettings: async (data) => {
    try {
      const res = await api.put('/locator/settings', data);
      set({ settings: res.data.settings });
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message;
      set({ error: msg });
    }
  },

  fetchSafePlaces: async () => {
    try {
      const res = await api.get('/locator/safe-places');
      set({ safePlaces: res.data.safe_places });
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message;
      set({ error: msg });
    }
  },

  createSafePlace: async (data) => {
    try {
      const res = await api.post('/locator/safe-places', data);
      set(state => ({ safePlaces: [...state.safePlaces, res.data.safe_place] }));
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message;
      set({ error: msg });
    }
  },

  deleteSafePlace: async (id) => {
    try {
      await api.delete(`/locator/safe-places/${id}`);
      set(state => ({ safePlaces: state.safePlaces.filter(sp => sp.id !== id) }));
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message;
      set({ error: msg });
    }
  },

  handleWsMessage: (message) => {
    switch (message.type) {
      case 'LOCATION_UPDATED':
        set(state => {
          const existing = state.locations.findIndex(l => l.user_id === message.payload.user_id);
          if (existing >= 0) {
            const updated = [...state.locations];
            updated[existing] = message.payload;
            return { locations: updated };
          }
          return { locations: [...state.locations, message.payload] };
        });
        break;

      case 'SAFE_PLACE_CREATED':
        set(state => {
          const exists = state.safePlaces.some(sp => sp.id === message.payload.id);
          if (exists) return state;
          return { safePlaces: [...state.safePlaces, message.payload] };
        });
        break;

      case 'SAFE_PLACE_UPDATED':
        set(state => ({
          safePlaces: state.safePlaces.map(sp =>
            sp.id === message.payload.id ? { ...sp, ...message.payload } : sp
          ),
        }));
        break;

      case 'SAFE_PLACE_DELETED':
        set(state => ({
          safePlaces: state.safePlaces.filter(sp => sp.id !== message.payload.id),
        }));
        break;
    }
  },
}));
